import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AppLayout } from "../components/app/AppLayout";
import { aiBulkApi, facilitiesApi, auth } from "../lib/api";
import { PlanLadder } from "../components/app/PlanLadder";
import { TID } from "../constants/testIds";
import {
  Sparkles, Send, Loader2, X, Search, Plus, CheckCircle2, AlertTriangle,
  ArrowRight, RotateCw, ChevronDown, ChevronUp, MessageSquare, MapPin,
  Ban, Trash2,
} from "lucide-react";
import { getApiError } from "../lib/utils";

const LS_KEY = "nmi_ai_edit_ids";

const fmt = (v) => {
  if (v == null || v === "") return <span className="italic text-slate-400">empty</span>;
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
};

// ============================================================================
// Working-set panel — pick which facilities the assistant should operate on
// ============================================================================

const WorkingSetPanel = ({ ids, setIds, expanded, setExpanded, refreshTick = 0 }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [details, setDetails] = useState({}); // fid -> {name, town, region}
  const [searching, setSearching] = useState(false);

  // Hydrate details for ids not yet resolved
  useEffect(() => {
    const missing = ids.filter((id) => !details[id]);
    if (missing.length === 0) return;
    Promise.all(
      missing.map((id) => facilitiesApi.get(id).catch(() => null))
    ).then((rows) => {
      const next = { ...details };
      rows.forEach((r) => { if (r) next[r.facility_id] = r; });
      setDetails(next);
    });
    // eslint-disable-next-line
  }, [ids]);

  // When the parent bumps refreshTick (e.g. after an AI Apply), re-fetch ALL details
  useEffect(() => {
    if (refreshTick === 0 || ids.length === 0) return;
    Promise.all(
      ids.map((id) => facilitiesApi.get(id).catch(() => null))
    ).then((rows) => {
      const next = {};
      rows.forEach((r) => { if (r) next[r.facility_id] = r; });
      setDetails(next);
    });
    // eslint-disable-next-line
  }, [refreshTick]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const d = await facilitiesApi.list({ q: q.trim(), page: 1, page_size: 8 });
        if (!cancelled) setResults(d.items || []);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const add = (f) => {
    if (ids.includes(f.facility_id)) return;
    setIds([...ids, f.facility_id]);
    setDetails((d) => ({ ...d, [f.facility_id]: f }));
    setQ(""); setResults([]);
  };
  const remove = (id) => setIds(ids.filter((x) => x !== id));
  const clear = () => setIds([]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button
        data-testid="ai-edit-workingset-toggle"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-nmi-100">
            <MessageSquare size={14} className="text-nmi-700" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Working set</div>
            <div className="text-sm font-semibold text-slate-900">
              {ids.length === 0 ? "No facilities chosen" : `${ids.length} facilit${ids.length === 1 ? "y" : "ies"} selected`}
            </div>
          </div>
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {expanded && (
        <div className="border-t border-slate-200 p-4 space-y-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="ai-edit-facility-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, town or ID…"
              className="w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-nmi-600 focus:ring-2 focus:ring-nmi-600/20"
            />
            {searching && (
              <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
            )}
          </div>
          {results.length > 0 && (
            <div className="max-h-56 overflow-y-auto rounded-md border border-slate-200">
              {results.map((r) => {
                const included = ids.includes(r.facility_id);
                return (
                  <button
                    key={r.facility_id}
                    data-testid={`ai-edit-add-${r.facility_id}`}
                    onClick={() => add(r)}
                    disabled={included}
                    className="flex w-full items-start justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left last:border-0 hover:bg-nmi-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-slate-900">{r.name}</div>
                      <div className="text-[11px] text-slate-500">{r.category} · {r.town || "—"}, {r.region || "—"}</div>
                    </div>
                    {included ? (
                      <CheckCircle2 size={16} className="mt-0.5 text-emerald-600" />
                    ) : (
                      <Plus size={16} className="mt-0.5 text-nmi-700" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                Selected {ids.length > 0 && <>({ids.length})</>}
              </div>
              {ids.length > 0 && (
                <button onClick={clear} className="text-[11px] font-semibold text-slate-500 underline hover:text-red-700">
                  Clear all
                </button>
              )}
            </div>
            {ids.length === 0 ? (
              <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-xs text-slate-500">
                Search above, or start from the Facilities page — the "AI Edit" button carries your selection here.
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200">
                {ids.map((id) => {
                  const d = details[id];
                  return (
                    <div
                      key={id}
                      data-testid={`ai-edit-chip-${id}`}
                      className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-1.5 text-xs last:border-0 hover:bg-slate-50"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-semibold text-slate-900">
                          {d?.name || <span className="italic text-slate-500">Loading…</span>}
                        </div>
                        <div className="truncate text-[10px] text-slate-500">
                          {d?.category || "—"} · {d?.town || "—"}, {d?.region || "—"}
                          {d?.status && d.status !== "ACTIVE" && (
                            <span className="ml-1 rounded bg-red-100 px-1 py-0.5 font-bold text-red-800">{d.status}</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => remove(id)}
                        className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-red-700"
                        aria-label="Remove"
                      >
                        <X size={12} strokeWidth={2.5} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Assistant message renderers
// ============================================================================

const AssistantBubble = ({ children }) => (
  <div className="flex gap-2.5">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-nmi-700 text-white shadow-sm">
      <Sparkles size={14} strokeWidth={2.5} />
    </div>
    <div className="max-w-[85%] rounded-2xl rounded-tl-md bg-white border border-slate-200 px-4 py-3 shadow-sm">
      {children}
    </div>
  </div>
);

const UserBubble = ({ children }) => (
  <div className="flex justify-end">
    <div className="max-w-[85%] rounded-2xl rounded-tr-md bg-nmi-700 px-4 py-2.5 text-sm text-white shadow-sm">
      {children}
    </div>
  </div>
);

const QuestionsCard = ({ questions, answers, setAnswers, onSubmit, submitting }) => {
  const answered = questions.every((q) => answers[q.id]);
  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500">
        I need a bit more info before I can safely propose changes.
      </div>
      {questions.map((q, qi) => (
        <div key={q.id}>
          <div className="mb-1.5 text-sm font-bold text-slate-900">
            {qi + 1}. {q.question}
          </div>
          <div className="space-y-1.5">
            {q.options.map((o) => {
              const selected = answers[q.id] === o.key;
              return (
                <label
                  key={o.key}
                  data-testid={TID.aiBulkEditOption(q.id, o.key)}
                  className={`flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-xs transition-colors ${
                    selected ? "border-nmi-600 bg-nmi-50 text-nmi-900" : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="radio"
                    name={q.id}
                    className="mt-0.5"
                    checked={selected}
                    onChange={() => setAnswers((a) => ({ ...a, [q.id]: o.key }))}
                  />
                  <div>
                    <span className="mr-1.5 font-mono font-bold uppercase">{o.key}.</span>
                    <span>{o.label}</span>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      ))}
      <div className="flex justify-end">
        <button
          data-testid={TID.aiBulkEditContinue}
          onClick={onSubmit}
          disabled={!answered || submitting}
          className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-nmi-800 disabled:opacity-50"
        >
          {submitting ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} strokeWidth={2.5} />}
          Continue
        </button>
      </div>
    </div>
  );
};

const PlanCard = ({ plan, warnings, selectedRows, setSelectedRows, onApply, applying }) => {
  const toggle = (fid) => setSelectedRows((s) => {
    const n = new Set(s); if (n.has(fid)) n.delete(fid); else n.add(fid); return n;
  });
  const applyCount = selectedRows.size;
  return (
    <div className="space-y-3">
      {warnings.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <ul className="list-disc space-y-0.5 pl-4">
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
      <div className="text-xs text-slate-700">
        Here's the plan — <b>{plan.length}</b> facilit{plan.length === 1 ? "y" : "ies"} will change.
        Uncheck any row to skip it. Nothing happens until you hit <b>Apply</b>.
      </div>
      {plan.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-center text-xs text-slate-600">
          No changes proposed. Try rephrasing.
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="w-8 px-2 py-1.5"></th>
                <th className="px-2 py-1.5 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Facility</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Changes</th>
              </tr>
            </thead>
            <tbody>
              {plan.map((row) => {
                const checked = selectedRows.has(row.facility_id);
                return (
                  <tr key={row.facility_id} className={`border-t border-slate-200 ${checked ? "" : "opacity-40"}`}>
                    <td className="px-2 py-1.5 align-top">
                      <input type="checkbox" checked={checked} onChange={() => toggle(row.facility_id)} />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <div className="font-semibold text-slate-900">{row.name}</div>
                      <div className="font-mono text-[10px] text-slate-500">{row.facility_id}</div>
                      {row.confidence && (
                        <div className="mt-1 inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider">
                          <span className={
                            row.confidence === "high" ? "text-emerald-700"
                            : row.confidence === "medium" ? "text-amber-700"
                            : "text-red-700"
                          }>{row.confidence}</span>
                          <span className="text-slate-500">confidence</span>
                        </div>
                      )}
                      {row.context && (
                        <div className="mt-1 text-[10px] text-slate-500">
                          {[row.context.region, row.context.town, row.context.location].filter(Boolean).join(" · ")}
                        </div>
                      )}
                      {row.note && <div className="mt-1 text-[10px] italic text-slate-500">{row.note}</div>}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <div className="space-y-1">
                        {Object.entries(row.changes).map(([field, delta]) => (
                          <div key={field}>
                            <span className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-700">
                              {field}
                            </span>
                            <div className="mt-0.5 flex flex-wrap items-center gap-1">
                              <span className="rounded bg-red-50 px-1 py-0.5 text-red-800 line-through">{fmt(delta.old)}</span>
                              <ArrowRight size={10} className="text-slate-400" />
                              <span className="rounded bg-emerald-50 px-1 py-0.5 font-semibold text-emerald-800">{fmt(delta.new)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-end">
        <button
          data-testid={TID.aiBulkEditApply}
          onClick={() => {
            if (applyCount === 0) return;
            if (!window.confirm(`Apply changes to ${applyCount} facilities?`)) return;
            onApply();
          }}
          disabled={applyCount === 0 || applying}
          className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-nmi-800 disabled:opacity-50"
        >
          {applying ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} strokeWidth={2.5} />}
          Apply to {applyCount}
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// Main page — ChatGPT-style conversation
// ============================================================================

export default function AiEdit() {
  const [ids, setIds] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  // Expand working set on first mount if we hydrated with a selection from Facilities
  const [panelExpanded, setPanelExpanded] = useState(true);
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      role: "assistant",
      kind: "text",
      body: (
        <div className="space-y-2 text-sm text-slate-800">
          <div className="font-heading text-base font-bold text-slate-900">
            Hi 👋 I'm the NMI Bulk Edit assistant.
          </div>
          <p>
            Add facilities to your working set above, then describe the change you'd like. If the data is branched or
            ambiguous I'll ask short multiple-choice questions before proposing any edits.
          </p>
          <p className="text-xs text-slate-500">
            Try: <i>"Add PREMIER to all these pharmacies"</i>, <i>"Update working hours to 8am–8pm"</i>, or
            <i> "Set the relationship manager to Jane Doe"</i>.
          </p>
        </div>
      ),
    },
  ]);
  const [applyTick, setApplyTick] = useState(0);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [instructionInFlight, setInstructionInFlight] = useState("");
  const scrollRef = useRef(null);

  // Persist selected ids.  Facility IDs are NOT sensitive (they appear in
  // URLs and are shown all over the app), so localStorage is fine here —
  // the "insecure storage" lint hit is a false positive for this key.
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(ids)); }
    catch (e) { console.warn("[ai-edit] failed to persist selection:", e); }
  }, [ids]);

  // Auto-scroll on new message
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const push = (msg) => setMessages((m) => [...m, { id: `m_${m.length}_${Date.now()}`, ...msg }]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    if (ids.length === 0) {
      toast.error("Add at least one facility to your working set first.");
      setPanelExpanded(true);
      return;
    }
    setInput("");
    push({ role: "user", kind: "text", body: text });
    setInstructionInFlight(text);
    await consultAndRender({ instruction: text, answers: {}, session_id: null });
  };

  const consultAndRender = async ({ instruction, answers, session_id }) => {
    setBusy(true);
    try {
      const r = await aiBulkApi.consult({
        instruction,
        facility_ids: ids,
        answers,
        session_id,
      });
      setSessionId(r.session_id);
      if (r.type === "questions") {
        if (!r.questions || r.questions.length === 0) {
          push({ role: "assistant", kind: "text", body: (
            <div className="text-sm text-slate-700">I couldn't figure out what to ask — please rephrase or add more detail.</div>
          )});
        } else {
          push({
            role: "assistant",
            kind: "questions",
            data: { questions: r.questions, answers: {}, instruction, session_id: r.session_id, resolved: false },
          });
        }
      } else if (r.type === "plan") {
        push({
          role: "assistant",
          kind: "plan",
          data: {
            plan: r.plan || [],
            warnings: r.warnings || [],
            selectedRows: new Set((r.plan || []).map((p) => p.facility_id)),
            resolved: false,
            instruction,
          },
        });
      }
    } catch (e) {
      toast.error(getApiError(e, "Assistant error"));
      push({ role: "assistant", kind: "text", body: (
        <div className="text-sm text-red-700">Something went wrong contacting the assistant. Please try again.</div>
      )});
    } finally { setBusy(false); }
  };

  const submitAnswers = async (msgId) => {
    const msg = messages.find((m) => m.id === msgId);
    if (!msg) return;
    const { instruction, answers, session_id, questions } = msg.data;
    // Freeze this question card in the transcript
    setMessages((all) => all.map((m) => m.id === msgId ? { ...m, data: { ...m.data, resolved: true } } : m));
    // Add a user "message" summarising their answers
    const summary = questions.map((q) => {
      const chosen = q.options.find((o) => o.key === answers[q.id]);
      return `Q${questions.indexOf(q) + 1}: ${chosen?.label || answers[q.id]}`;
    }).join(" · ");
    push({ role: "user", kind: "text", body: summary });
    await consultAndRender({ instruction, answers, session_id });
  };

  const setMsgQuestionAnswers = (msgId, updater) => {
    setMessages((all) => all.map((m) => {
      if (m.id !== msgId) return m;
      const nextAns = typeof updater === "function" ? updater(m.data.answers) : updater;
      return { ...m, data: { ...m.data, answers: nextAns } };
    }));
  };

  const setMsgPlanSelection = (msgId, updater) => {
    setMessages((all) => all.map((m) => {
      if (m.id !== msgId) return m;
      const next = typeof updater === "function" ? updater(m.data.selectedRows) : updater;
      return { ...m, data: { ...m.data, selectedRows: next } };
    }));
  };

  const applyPlan = async (msgId) => {
    const msg = messages.find((m) => m.id === msgId);
    if (!msg) return;
    const rows = msg.data.plan.filter((p) => msg.data.selectedRows.has(p.facility_id));
    if (rows.length === 0) return;
    setBusy(true);
    setMessages((all) => all.map((m) => m.id === msgId ? { ...m, data: { ...m.data, resolved: true } } : m));
    try {
      const patches = rows.map((row) => ({
        facility_id: row.facility_id,
        patch: Object.fromEntries(Object.entries(row.changes).map(([field, delta]) => [field, delta.new])),
      }));
      const r = await aiBulkApi.apply({ patches, instruction: msg.data.instruction });
      push({
        role: "assistant",
        kind: "result",
        body: (
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center gap-2 text-emerald-800">
              <CheckCircle2 size={16} />
              <b>Applied {r.applied} of {r.total} facilities.</b>
            </div>
            <div className="text-xs text-slate-600">
              Skipped (no change): {r.skipped} · Errors: {r.errors.length}
            </div>
            {r.errors.length > 0 && (
              <details className="text-xs text-red-700">
                <summary className="cursor-pointer font-semibold">Errors ({r.errors.length})</summary>
                <ul className="mt-1 list-disc pl-4 space-y-0.5">
                  {r.errors.map((e, i) => <li key={i}><span className="font-mono">{e.facility_id}</span>: {e.error}</li>)}
                </ul>
              </details>
            )}
            <div className="text-xs text-slate-500">Ask me anything else — your working set is still ready.</div>
          </div>
        ),
      });
      toast.success(`Applied ${r.applied} facilities`);
      setApplyTick((t) => t + 1);
    } catch (e) {
      toast.error(getApiError(e, "Apply failed"));
    } finally { setBusy(false); }
  };

  const startOver = () => {
    setSessionId(null);
    setInstructionInFlight("");
    setMessages((m) => [m[0]]); // keep welcome
  };

  const [fillLimit, setFillLimit] = useState(100);
  const [fillingDistricts, setFillingDistricts] = useState(false);
  const [bulkAction, setBulkAction] = useState(null); // 'suspend' | 'reactivate' | 'delete' | null

  const me = auth.me();
  const isAdmin = me?.role === "ADMIN" || me?.role === "SUPER_ADMIN";
  const isSuperAdmin = me?.role === "SUPER_ADMIN";
  const runFillDistricts = async () => {
    setFillingDistricts(true);
    try {
      push({
        role: "user",
        kind: "text",
        body: `AI Fill Missing Districts (limit ${fillLimit}${ids.length ? `, scoped to ${ids.length} facilities` : " · network-wide"})`,
      });
      const r = await aiBulkApi.fillDistricts({
        facility_ids: ids.length ? ids : null,
        limit: fillLimit,
        only_missing: true,
      });
      const plan = (r.plan || []).map((row) => ({
        facility_id: row.facility_id,
        name: row.name,
        changes: row.changes,
        confidence: row.confidence,
        note: row.note,
        context: row.context,
      }));
      if (plan.length === 0) {
        push({
          role: "assistant",
          kind: "text",
          body: (
            <div className="text-sm text-slate-700">
              Scanned <b>{r.total_scanned}</b> facilities but couldn't propose any district — either they already
              have a district, or the location signal was too thin. Try widening the scope or increasing the limit.
              {r.warnings?.length > 0 && (
                <div className="mt-2 text-xs text-amber-700">Warnings: {r.warnings.join(" · ")}</div>
              )}
            </div>
          ),
        });
      } else {
        push({
          role: "assistant",
          kind: "plan",
          data: {
            plan,
            warnings: r.warnings || [],
            selectedRows: new Set(plan.filter((p) => p.confidence !== "low").map((p) => p.facility_id)),
            resolved: false,
            instruction: "AI Fill Missing Districts",
          },
        });
      }
    } catch (e) {
      toast.error(getApiError(e, "Fill districts failed"));
    } finally {
      setFillingDistricts(false);
    }
  };

  return (
    <AppLayout>
      <div className="mx-auto flex h-[calc(100vh-3rem)] max-w-4xl flex-col md:h-[calc(100vh-2rem)]">
        {/* Header */}
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-nmi-700">
              AI Assistant · Claude Sonnet 4.5
            </div>
            <h1 className="font-heading text-2xl font-black tracking-tight text-slate-900 md:text-3xl">
              AI Edit
            </h1>
          </div>
          <button
            data-testid={TID.aiBulkEditRestart}
            onClick={startOver}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <RotateCw size={12} /> New chat
          </button>
        </div>

        {/* Working set */}
        <div className="mb-3">
          <WorkingSetPanel
            ids={ids}
            setIds={setIds}
            expanded={panelExpanded}
            setExpanded={setPanelExpanded}
            refreshTick={applyTick}
          />
        </div>

        {/* Quick actions */}
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white p-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 pl-1">Quick actions</div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1 text-[11px] text-slate-600">
              limit
              <input
                type="number"
                min={1}
                max={500}
                value={fillLimit}
                onChange={(e) => setFillLimit(Math.max(1, Math.min(500, Number(e.target.value) || 100)))}
                className="w-16 rounded-md border border-slate-200 bg-white px-1.5 py-1 text-xs outline-none focus:border-nmi-600"
                data-testid="fill-districts-limit"
              />
            </label>
            <button
              data-testid="fill-districts-btn"
              onClick={runFillDistricts}
              disabled={fillingDistricts || busy}
              className="inline-flex items-center gap-1.5 rounded-md border border-nmi-600 bg-white px-3 py-1.5 text-xs font-semibold text-nmi-700 hover:bg-nmi-50 disabled:opacity-50"
              title={ids.length ? "Scoped to the facilities in your working set" : "Scans all facilities with missing district"}
            >
              {fillingDistricts ? <Loader2 size={13} className="animate-spin" /> : <MapPin size={13} strokeWidth={2.5} />}
              AI Fill Missing Districts
            </button>
            {isAdmin && (
              <>
                <span className="mx-1 h-4 w-px bg-slate-200" />
                <button
                  data-testid="ai-edit-bulk-suspend"
                  onClick={() => setBulkAction("suspend")}
                  disabled={ids.length === 0 || busy}
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-500 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-40"
                  title={ids.length === 0 ? "Select facilities first" : `Suspend ${ids.length} selected`}
                >
                  <Ban size={13} strokeWidth={2.5} /> Bulk suspend
                </button>
                <button
                  data-testid="ai-edit-bulk-reactivate"
                  onClick={() => setBulkAction("reactivate")}
                  disabled={ids.length === 0 || busy}
                  className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                  title={ids.length === 0 ? "Select facilities first" : `Reactivate ${ids.length} selected`}
                >
                  <CheckCircle2 size={13} strokeWidth={2.5} /> Bulk reactivate
                </button>
              </>
            )}
            {isSuperAdmin && (
              <button
                data-testid="ai-edit-bulk-delete"
                onClick={() => setBulkAction("delete")}
                disabled={ids.length === 0 || busy}
                className="inline-flex items-center gap-1.5 rounded-md border border-red-600 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-40"
                title={ids.length === 0 ? "Select facilities first" : `Delete ${ids.length} selected`}
              >
                <Trash2 size={13} strokeWidth={2.5} /> Bulk delete
              </button>
            )}
            <span className="text-[10px] text-slate-500">
              {ids.length ? `scoped to ${ids.length} selected` : "network-wide (missing only)"}
            </span>
          </div>
        </div>

        {bulkAction && (
          <AiEditBulkModal
            mode={bulkAction}
            facilityIds={ids}
            onClose={() => setBulkAction(null)}
            onDone={(clearWorkingSet) => {
              setBulkAction(null);
              if (clearWorkingSet) setIds([]);
              setApplyTick((t) => t + 1);
            }}
          />
        )}

        {/* Chat */}
        <div
          ref={scrollRef}
          data-testid="ai-edit-chat"
          className="flex-1 space-y-4 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/50 p-4"
        >
          {messages.map((m) => {
            if (m.role === "user") return <UserBubble key={m.id}>{m.body}</UserBubble>;
            if (m.kind === "questions") {
              const d = m.data;
              return (
                <AssistantBubble key={m.id}>
                  {d.resolved ? (
                    <div className="text-xs italic text-slate-500">Answered ↓</div>
                  ) : (
                    <QuestionsCard
                      questions={d.questions}
                      answers={d.answers}
                      setAnswers={(u) => setMsgQuestionAnswers(m.id, u)}
                      onSubmit={() => submitAnswers(m.id)}
                      submitting={busy}
                    />
                  )}
                </AssistantBubble>
              );
            }
            if (m.kind === "plan") {
              const d = m.data;
              return (
                <AssistantBubble key={m.id}>
                  {d.resolved ? (
                    <div className="text-xs italic text-slate-500">Applied ↓</div>
                  ) : (
                    <PlanCard
                      plan={d.plan}
                      warnings={d.warnings}
                      selectedRows={d.selectedRows}
                      setSelectedRows={(u) => setMsgPlanSelection(m.id, u)}
                      onApply={() => applyPlan(m.id)}
                      applying={busy}
                    />
                  )}
                </AssistantBubble>
              );
            }
            return <AssistantBubble key={m.id}>{m.body}</AssistantBubble>;
          })}
          {busy && (
            <AssistantBubble>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Loader2 size={12} className="animate-spin" /> Thinking…
              </div>
            </AssistantBubble>
          )}
        </div>

        {/* Composer */}
        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
          <div className="flex items-end gap-2">
            <textarea
              data-testid={TID.aiBulkEditInstruction}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              placeholder={ids.length === 0
                ? "Add facilities above, then describe the change…"
                : "Message the assistant… (Enter to send, Shift+Enter for newline)"}
              className="flex-1 resize-none rounded-md border-0 bg-transparent px-2 py-2 text-sm outline-none placeholder:text-slate-400"
              style={{ minHeight: 40, maxHeight: 160 }}
            />
            <button
              data-testid={TID.aiBulkEditStart}
              onClick={send}
              disabled={!input.trim() || busy || ids.length === 0}
              className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-md bg-nmi-700 px-3 text-xs font-semibold text-white hover:bg-nmi-800 disabled:opacity-40"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} strokeWidth={2.5} />}
              <span className="hidden md:inline">Send</span>
            </button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

// ============================================================================
// Bulk action modal for AI Edit (suspend / reactivate / delete)
// ============================================================================
const AiEditBulkModal = ({ mode, facilityIds, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const cfg = {
    suspend: {
      title: `Suspend ${facilityIds.length} facilit${facilityIds.length === 1 ? "y" : "ies"}?`,
      body: "Selected providers will be marked SUSPENDED, alert-subscribed users notified in real time, and the reason attached to each record.",
      Icon: Ban,
      ring: "border-red-300",
      iconBg: "bg-red-100", iconFg: "text-red-700",
      btn: "bg-red-600 hover:bg-red-700",
      label: `Yes, suspend ${facilityIds.length}`,
      requireReason: true,
    },
    reactivate: {
      title: `Reactivate ${facilityIds.length} facilit${facilityIds.length === 1 ? "y" : "ies"}?`,
      body: "Selected providers will be marked ACTIVE and appear in Telegram lookups again. Everyone with alerts on will be notified.",
      Icon: CheckCircle2,
      ring: "border-emerald-300",
      iconBg: "bg-emerald-100", iconFg: "text-emerald-700",
      btn: "bg-emerald-600 hover:bg-emerald-700",
      label: `Yes, reactivate ${facilityIds.length}`,
      requireReason: true,
    },
    delete: {
      title: `Delete ${facilityIds.length} facilit${facilityIds.length === 1 ? "y" : "ies"}?`,
      body: "This permanently removes the selected providers. A snapshot is kept in the audit log for reference, but the records will no longer appear anywhere in the app.",
      Icon: Trash2,
      ring: "border-red-300",
      iconBg: "bg-red-100", iconFg: "text-red-700",
      btn: "bg-red-600 hover:bg-red-700",
      label: `Yes, delete ${facilityIds.length}`,
      requireReason: false,
    },
  }[mode];

  const submit = async () => {
    const r = reason.trim();
    if (cfg.requireReason && !r) {
      toast.error("Reason is required");
      return;
    }
    setBusy(true);
    try {
      if (mode === "delete") {
        const res = await facilitiesApi.bulkDelete(facilityIds, r || null);
        toast.success(`Deleted ${res.deleted} facilit${res.deleted === 1 ? "y" : "ies"}${res.not_found?.length ? ` · ${res.not_found.length} not found` : ""}`);
      } else {
        const res = await facilitiesApi.bulkStatus(
          facilityIds,
          mode === "suspend" ? "SUSPENDED" : "ACTIVE",
          r,
        );
        const parts = [
          `${res.changed} ${mode === "suspend" ? "suspended" : "reactivated"}`,
          res.skipped_already_in_that_state ? `${res.skipped_already_in_that_state} already in state` : null,
          res.not_found?.length ? `${res.not_found.length} not found` : null,
        ].filter(Boolean).join(" · ");
        toast.success(parts);
      }
      onDone?.(true);
    } catch (e) {
      toast.error(getApiError(e, `${mode} failed`));
    } finally {
      setBusy(false);
    }
  };

  const Icon = cfg.Icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={() => !busy && onClose()}>
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full max-w-md overflow-hidden rounded-lg border-2 ${cfg.ring} bg-white shadow-2xl`}
        data-testid={`ai-edit-bulk-${mode}-modal`}
      >
        <div className="flex items-start gap-3 border-b border-slate-200 p-5">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${cfg.iconBg}`}>
            <Icon size={20} className={cfg.iconFg} />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-slate-900">{cfg.title}</h3>
            <p className="mt-1 text-xs text-slate-600">{cfg.body}</p>
          </div>
        </div>

        <div className="p-5">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            Reason {cfg.requireReason ? (<span className="text-red-500">*</span>) : "(optional)"}
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              mode === "suspend"
                ? "e.g. Contract renegotiation, non-payment, duplicate rows…"
                : mode === "reactivate"
                ? "e.g. Contract renewed, back on the network as of today…"
                : "e.g. Duplicates, decommissioned, closed…"
            }
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-nmi-600"
            rows={3}
            disabled={busy}
            maxLength={500}
            autoFocus
            data-testid={`ai-edit-bulk-${mode}-reason`}
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            onClick={onClose}
            disabled={busy}
            data-testid={`ai-edit-bulk-${mode}-cancel`}
            className="rounded-md border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || (cfg.requireReason && !reason.trim())}
            data-testid={`ai-edit-bulk-${mode}-submit`}
            className={`inline-flex items-center gap-1.5 rounded-md ${cfg.btn} px-4 py-2 text-xs font-bold uppercase tracking-wider text-white disabled:opacity-40`}
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} strokeWidth={2.5} />}
            {cfg.label}
          </button>
        </div>
      </div>
    </div>
  );
};

